import React, { useState, useEffect, useRef } from "react";
import {
    MenuItem,
    TextField,
    Checkbox,
    FormControlLabel,
    CircularProgress,
    Tooltip,
    InputAdornment,
    Select,
    FormControl,
    InputLabel,
    FormHelperText,
    Box,
    Chip,
} from "@mui/material";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useNavigate, useLocation } from "react-router-dom";

import { firebaseConfig } from '../../firebase';
import { toast } from 'react-toastify';
import configData from "../../configure";
import { useAuth } from "../../AuthContext";
import { studentFields } from "./configs/registration/studentFields";
import { facultyFields } from "./configs/registration/facultyFields";
import { staffFields } from "./configs/registration/staffFields";
import { adminFields } from "./configs/registration/adminFields";
import './UserRegistrationModule.css';

// Section icons
const sectionIcons = {
    "Basic Information": "👤",
    "Contact Information": "📞",
    "Parent/Guardian Details": "👨‍👩‍👧",
    "Other Details": "📋",
    "Academic Details": "🎓",
    "Faculty Details": "🏫",
    "Staff Details": "🔧",
    "Admin Details": "🛡️",
    "Employment Details": "💼",
    "Professional Details": "📐",
};

const fieldConfigs = {
    STUDENT: studentFields,
    FACULTY: facultyFields,
    STAFF: staffFields,
    ADMIN: adminFields,
};

const KV_ORG_ID = '62d3b1f4-e453-4933-bfcf-7b5adf5b4e83';
const PHONE_FIELD_NAMES = ["phoneNumber", "contactNumber", "alternatePhoneNumber", "fatherContact", "motherContact"];

const UserRegistrationModule = ({ userType, orgId, onCancel, lockedFacultyType, onSuccess }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { selectedClass, lockedFacultyType: stateLockedFacultyType, supervisorId: stateSupervisorId, supervisorName: stateSupervisorName } = location.state || {};
    const finalLockedFacultyType = lockedFacultyType || stateLockedFacultyType;
    const { axios, userRole, facultyInfo } = useAuth();

    const [formData, setFormData] = useState({});
    const [loading, setLoading] = useState(false);
    const [duplicateErrors, setDuplicateErrors] = useState({}); // { email: 'msg', phoneNumber: 'msg' }
    const [fieldErrors, setFieldErrors] = useState({}); // { [fieldName]: 'validation message' }

    const getAssignedCenters = () => {
        const assignedStr = formData.assignedClasses || formData.responsibilities || '';
        if (!assignedStr) return [];
        return assignedStr.split(',')
            .map(c => c.trim())
            .filter(c => c.length > 0 && c.toLowerCase() !== 'main-region' && c.toLowerCase() !== 'main region');
    };

    // Class filtering states (only used for STUDENT)
    const [classesList, setClassesList] = useState([]);
    const [classesLoading, setClassesLoading] = useState(false);

    // ── Supervisor dropdown state ──────────────────────────────────────────
    const [supervisorOptions, setSupervisorOptions] = useState([]);
    const [supervisorLoading, setSupervisorLoading] = useState(false);
    const supervisorFetchRef = useRef(null);

    const isCustomOrg = orgId === '62d3b1f4-e453-4933-bfcf-7b5adf5b4e83';

    const currentFields = React.useMemo(() => {
        let rawFields = fieldConfigs[userType] || [];
        if (userType === 'STUDENT') {
            if (isCustomOrg) {
                const classOptions = [
                    "Nursery",
                    "LKG",
                    "UKG",
                    "Class 1",
                    "Class 2",
                    "Class 3",
                    "Class 4",
                    "Class 5",
                    "Class 6",
                    "Class 7",
                    "Class 8",
                    "Class 9",
                    "Class 10"
                ];
                rawFields = rawFields.map(section => {
                    if (section.section === "Basic Information") {
                        const hasClassName = section.fields.some(f => f.name === 'className');
                        if (!hasClassName) {
                            return {
                                ...section,
                                fields: [
                                    ...section.fields,
                                    {
                                        name: "className",
                                        label: "Class Name",
                                        required: true,
                                        type: "select",
                                        options: classOptions
                                    }
                                ]
                            };
                        }
                    }
                    return section;
                });
            }
        }
        if (userType === 'FACULTY' && userRole === 'FACULTY') {
            let allowedOptions = ["NIRVAHAK", "PARYAVEKSHAK"];
            if (facultyInfo?.facultyType === 'PARYAVEKSHAK') {
                allowedOptions = ["NIRVAHAK"];
            } else if (facultyInfo?.facultyType === 'SAMANVAYKARTA') {
                allowedOptions = ["NIRVAHAK", "PARYAVEKSHAK"];
            }
            return rawFields.map(section => {
                if (section.section === "Faculty Information") {
                    return {
                        ...section,
                        fields: section.fields.map(field => {
                            if (field.name === "facultyType") {
                                return {
                                    ...field,
                                    options: allowedOptions
                                };
                            }
                            return field;
                        })
                    };
                }
                return section;
            });
        }
        return rawFields;
    }, [userType, userRole, facultyInfo, isCustomOrg]);

    // Initialize form data
    useEffect(() => {
        const initialData = {};
        currentFields.forEach(section => {
            section.fields.forEach(field => {
                initialData[field.name] = field.type === 'checkbox' ? false : "";
            });
        });
        // Pre-fill locked faculty type from parent (e.g., KV Add New dialog or route state)
        if (finalLockedFacultyType && userType === 'FACULTY') {
            initialData.facultyType = finalLockedFacultyType;
        }
        // Pre-fill supervisorId from route state
        if (stateSupervisorId && userType === 'FACULTY') {
            initialData.supervisorId = stateSupervisorId;
        }
        // Pre-fill classId from route state
        if (userType === 'STUDENT') {
            initialData.classId = selectedClass || "";
        }
        setFormData(initialData);

        if (stateSupervisorId && stateSupervisorName) {
            setSupervisorOptions([{
                id: stateSupervisorId,
                name: stateSupervisorName,
                employeeId: '',
                phone: ''
            }]);
        }
    }, [userType, finalLockedFacultyType, stateSupervisorId, stateSupervisorName, selectedClass, currentFields]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        let sanitizedValue = type === 'checkbox' ? checked : value;

        // Common phone sanitization
        if (PHONE_FIELD_NAMES.includes(name)) {
            let digits = (typeof value === 'string' ? value : '').replace(/[^0-9]/g, "");
            // If pasted with country code +91 or leading 0, strip prefix if length exceeds 10
            if (digits.length === 12 && digits.startsWith("91")) {
                digits = digits.slice(2);
            } else if (digits.length === 11 && digits.startsWith("0")) {
                digits = digits.slice(1);
            }
            sanitizedValue = digits.slice(0, 10);
        }

        if (name === 'lastFourDigitsOfAadhaarNumber') {
            sanitizedValue = (typeof value === 'string' ? value : '').replace(/[^0-9]/g, "").slice(-4);
        }

        setFormData(prev => {
            const nextData = { ...prev, [name]: sanitizedValue };
            // Clear supervisorId whenever faculty type changes
            if (name === 'facultyType') {
                nextData.supervisorId = '';
            }
            return nextData;
        });

        // Clear duplicate error for this field when user edits it
        if (duplicateErrors[name]) {
            setDuplicateErrors(prev => { const e = { ...prev }; delete e[name]; return e; });
        }
        // Clear field validation error for this field when user edits it
        if (fieldErrors[name]) {
            setFieldErrors(prev => { const e = { ...prev }; delete e[name]; return e; });
        }
    };

    const handleKeyDown = (e, field) => {
        if (PHONE_FIELD_NAMES.includes(field.name)) {
            // Allow navigation and editing keys
            if ([8, 9, 13, 27, 46].includes(e.keyCode) ||
                (e.ctrlKey || e.metaKey) ||
                (e.keyCode >= 35 && e.keyCode <= 40)) {
                return;
            }
            // Block non-numeric keys
            if (!/^[0-9]$/.test(e.key)) {
                e.preventDefault();
                return;
            }
            // Prevent entering more than 10 numbers
            const input = e.target;
            const selectionLength = (input.selectionEnd || 0) - (input.selectionStart || 0);
            const currentDigits = (input.value || '').replace(/[^0-9]/g, '');
            if (currentDigits.length >= 10 && selectionLength === 0) {
                e.preventDefault();
            }
        }
    };

    const handleBlur = (field) => {
        const name = field.name;
        const rawVal = formData[name];
        const val = (rawVal !== undefined && rawVal !== null) ? String(rawVal).trim() : '';
        const isPhoneField = PHONE_FIELD_NAMES.includes(name);

        if (isPhoneField) {
            if (!val) {
                if (field.required) {
                    setFieldErrors(prev => ({ ...prev, [name]: `${field.label} is required` }));
                } else {
                    setFieldErrors(prev => { const e = { ...prev }; delete e[name]; return e; });
                }
            } else if (val.length < 10) {
                setFieldErrors(prev => ({ ...prev, [name]: `${field.label} cannot be less than 10 digits (must be exactly 10 digits)` }));
            } else if (val.length > 10) {
                setFieldErrors(prev => ({ ...prev, [name]: `${field.label} cannot be more than 10 digits (must be exactly 10 digits)` }));
            } else {
                setFieldErrors(prev => { const e = { ...prev }; delete e[name]; return e; });
            }
        }
    };

    // ── Fetch supervisors when facultyType changes (KV org only) ──────────────
    const isKVOrg = orgId === KV_ORG_ID;

    // supervisorFacultyType: the TYPE of faculty we need to list as supervisors
    const supervisorFacultyType = React.useMemo(() => {
        return formData.facultyType === 'NIRVAHAK'
            ? ['PARYAVEKSHAK', 'SAMANVAYKARTA']
            : formData.facultyType === 'PARYAVEKSHAK'
            ? ['SAMANVAYKARTA']
            : [];
    }, [formData.facultyType]);

    const supervisorFacultyLabel = React.useMemo(() => {
        return supervisorFacultyType
            .map(t => t.charAt(0) + t.slice(1).toLowerCase())
            .join('/');
    }, [supervisorFacultyType]);

    const supervisorDisabled = formData.facultyType === 'SAMANVAYKARTA';
    const supervisorIsDropdown = isKVOrg && supervisorFacultyType.length > 0;

    useEffect(() => {
        if (!supervisorIsDropdown || !orgId) {
            setSupervisorOptions([]);
            return;
        }

        // Cancel any previous in-flight fetch
        if (supervisorFetchRef.current) supervisorFetchRef.current.cancelled = true;
        const guard = { cancelled: false };
        supervisorFetchRef.current = guard;

        const fetchSupervisors = async () => {
            setSupervisorLoading(true);
            try {
                let lastEvaluatedKey = null;
                let allItems = [];
                const seenKeys = new Set();
                let isFirstPage = true;
                do {
                    const res = await axios.get(`${configData.GET_USERS_BY_TYPE}/${orgId}`, {
                        params: {
                            userType: 'FACULTY',
                            pageSize: isFirstPage ? 50 : 1000,
                            lastEvaluatedKey: lastEvaluatedKey || undefined
                        },
                        headers: { accept: 'application/hal+json' },
                    });
                    if (guard.cancelled) return;

                    const rawData = res.data;
                    const pageItems = Array.isArray(rawData)
                        ? rawData
                        : Array.isArray(rawData?.items)
                        ? rawData.items
                        : Array.isArray(rawData?.content)
                        ? rawData.content
                        : [];
                    allItems = allItems.concat(pageItems);

                    const filtered = allItems
                        .filter(item => {
                            const type = item?.facultyInfo?.facultyType;
                            return supervisorFacultyType.includes(type);
                        })
                        .map(item => ({
                            id: item?.user?.userId || '',
                            name: [item?.user?.firstName, item?.user?.middleName, item?.user?.lastName]
                                .filter(Boolean).join(' ').trim() || item?.user?.email || 'Unknown',
                            employeeId: item?.facultyInfo?.employeeId || '',
                            phone: item?.user?.phoneNumber || '',
                        }))
                        .filter(s => !!s.id);

                    setSupervisorOptions(filtered);

                    lastEvaluatedKey = rawData?.lastEvaluatedKey;
                    if (lastEvaluatedKey && seenKeys.has(lastEvaluatedKey)) break;
                    if (lastEvaluatedKey) seenKeys.add(lastEvaluatedKey);
                    isFirstPage = false;
                } while (lastEvaluatedKey);
            } catch (err) {
                if (!guard.cancelled) setSupervisorOptions([]);
            } finally {
                if (!guard.cancelled) setSupervisorLoading(false);
            }
        };

        fetchSupervisors();
        return () => { guard.cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [supervisorFacultyType, supervisorIsDropdown, orgId]);

    useEffect(() => {
        if (userType === 'STUDENT' && orgId && axios) {
            setClassesLoading(true);
            axios.get(`${configData.GET_CLASSES_BY_ORG}/${orgId}/metadata`, {
                headers: { accept: 'application/json' }
            })
            .then(res => {
                const list = res.data || [];
                setClassesList(list.map(c => {
                    const section = (c.sectionName && c.sectionName !== 'null' && c.sectionName !== 'undefined') ? String(c.sectionName).trim() : '';
                    return {
                        id: c.classId || c.id,
                        name: (section ? `${c.className} ${section}` : `${c.className || ''}`).trim() || 'Unnamed Class'
                    };
                }).sort((a, b) => a.name.localeCompare(b.name)));
            })
            .catch(err => console.error("Error fetching classes list in registration:", err))
            .finally(() => setClassesLoading(false));
        }
    }, [userType, orgId, axios]);

    const validate = () => {
        const errors = [];
        const newFieldErrors = {};

        currentFields.forEach(section => {
            section.fields.forEach(field => {
                const rawVal = formData[field.name];
                const val = (rawVal !== undefined && rawVal !== null) ? String(rawVal).trim() : '';
                const isPhoneField = PHONE_FIELD_NAMES.includes(field.name);

                if (field.required) {
                    const isDisabledSupervisor = field.name === 'supervisorId' && supervisorDisabled;
                    if (!isDisabledSupervisor && !val) {
                        const errMsg = `${field.label} is required`;
                        errors.push(errMsg);
                        newFieldErrors[field.name] = errMsg;
                    }
                }

                if (isPhoneField && val) {
                    if (val.length < 10) {
                        const errMsg = `${field.label} cannot be less than 10 digits (must be exactly 10 digits)`;
                        errors.push(errMsg);
                        newFieldErrors[field.name] = errMsg;
                    } else if (val.length > 10) {
                        const errMsg = `${field.label} cannot be more than 10 digits (must be exactly 10 digits)`;
                        errors.push(errMsg);
                        newFieldErrors[field.name] = errMsg;
                    }
                }
            });
        });

        setFieldErrors(newFieldErrors);
        return errors;
    };

    // ── Duplicate check for FACULTY registrations ────────────────────────────
    const checkDuplicateFaculty = async () => {
        const email = (formData.email || '').trim().toLowerCase();
        const phone = (formData.phoneNumber || formData.contactNumber || '').trim();
        const empId = (formData.employeeId || '').trim();

        if (!email && !phone && !empId) return {};

        try {
            let allItems = [];
            const res = await axios.get(`${configData.GET_USERS_BY_TYPE}/${orgId}`, {
                params: { userType: 'FACULTY', page: 1, pageSize: 2000 },
                headers: { accept: 'application/hal+json' },
            });
            const rawData = res.data;
            allItems = Array.isArray(rawData)
                ? rawData
                : Array.isArray(rawData?.items)
                ? rawData.items
                : Array.isArray(rawData?.content)
                ? rawData.content
                : [];

            const errors = {};
            for (const f of allItems) {
                const existingEmail = (f.user?.email || f.email || '').toLowerCase().trim();
                const existingPhone = (f.user?.phoneNumber || f.phoneNumber || '').trim();
                const existingEmpId = (f.facultyInfo?.employeeId || f.employeeId || '').trim();

                const inputEmpId = (formData.employeeId || '').trim();

                if (email && existingEmail && existingEmail === email) {
                    errors.email = `This email is already registered.`;
                }
                if (phone && existingPhone && existingPhone === phone) {
                    errors.phoneNumber = `This phone number is already registered.`;
                }
                if (inputEmpId && existingEmpId && existingEmpId === inputEmpId) {
                    errors.employeeId = `This employee ID is already registered.`;
                }
                // Stop as soon as all conflicts found
                if (errors.email && errors.phoneNumber && errors.employeeId) break;
            }
            return errors;
        } catch (err) {
            console.warn('[Registration] Duplicate check failed (non-blocking):', err.message);
            return {}; // Allow registration to proceed if check itself fails
        }
    };

    // ── Duplicate check for STUDENT registrations (admission number) ─────────
    const checkDuplicateStudent = async () => {
        const inputAdmissionId = (formData.admissionId || '').trim().toLowerCase();
        if (!inputAdmissionId) return {};

        try {
            let allItems = [];
            let page = 1;
            const pageSize = 500;
            let hasMore = true;
            while (hasMore) {
                const res = await axios.get(`${configData.GET_USERS_BY_TYPE}/${orgId}`, {
                    params: { userType: 'STUDENT', page, pageSize },
                    headers: { accept: 'application/hal+json' },
                });
                const rawData = res.data;
                const items = Array.isArray(rawData)
                    ? rawData
                    : Array.isArray(rawData?.items)
                    ? rawData.items
                    : Array.isArray(rawData?.content)
                    ? rawData.content
                    : [];
                allItems = allItems.concat(items);
                hasMore = items.length === pageSize;
                page++;
            }

            for (const s of allItems) {
                const existingAdmId = (
                    s?.studentInfo?.admissionId ||
                    s?.studentInfo?.admissionNumber ||
                    s?.admissionId || ''
                ).trim().toLowerCase();
                if (existingAdmId && existingAdmId === inputAdmissionId) {
                    return { admissionId: `Admission number "${formData.admissionId.trim()}" is already registered for another student.` };
                }
            }
            return {};
        } catch (err) {
            console.warn('[Registration] Student duplicate check failed (non-blocking):', err.message);
            return {}; // Allow registration to proceed if check itself fails
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const errors = validate();
        if (errors.length > 0) {
            const phoneError = errors.find(err =>
                err.toLowerCase().includes("10 digits") ||
                err.toLowerCase().includes("10-digit") ||
                err.toLowerCase().includes("more than 10") ||
                err.toLowerCase().includes("less than 10") ||
                err.toLowerCase().includes("mobile") ||
                err.toLowerCase().includes("phone") ||
                err.toLowerCase().includes("contact")
            );
            if (phoneError) {
                toast.error(phoneError);
            } else {
                toast.warn(errors[0] || "Please fix validation errors.");
            }
            return;
        }

        // Duplicate check for STUDENT (admission number uniqueness)
        if (userType === 'STUDENT') {
            setLoading(true);
            const dupErrors = await checkDuplicateStudent();
            if (Object.keys(dupErrors).length > 0) {
                setDuplicateErrors(dupErrors);
                const messages = Object.values(dupErrors);
                toast.error(
                    <div>
                        <strong>Duplicate admission number detected:</strong>
                        <ul style={{ margin: '4px 0 0 0', paddingLeft: '18px' }}>
                            {messages.map((m, i) => <li key={i}>{m}</li>)}
                        </ul>
                    </div>,
                    { autoClose: 6000 }
                );
                setLoading(false);
                return;
            }
            setLoading(false);
        }

        // Duplicate check for FACULTY
        if (userType === 'FACULTY') {
            setLoading(true);
            const dupErrors = await checkDuplicateFaculty();
            if (Object.keys(dupErrors).length > 0) {
                setDuplicateErrors(dupErrors);
                const messages = Object.values(dupErrors);
                toast.error(
                    <div>
                        <strong>Duplicate employee detected:</strong>
                        <ul style={{ margin: '4px 0 0 0', paddingLeft: '18px' }}>
                            {messages.map((m, i) => <li key={i}>{m}</li>)}
                        </ul>
                    </div>
                );
                setLoading(false);
                return;
            }
            setLoading(false);
        }

        setLoading(true);
        const defaultPassword = "egurukul@123456";

        try {
            // Prepare Payload
            const currentTime = new Date().toISOString();
            
            // Safe Date Formatting utility
            const toSafeISO = (dateVal) => {
                if (!dateVal) return null;
                const d = new Date(dateVal);
                return isNaN(d.getTime()) ? null : d.toISOString();
            };

            const dob = toSafeISO(formData.dob);
            const joiningDate = formData.joiningDate || formData.admissionDate;
            const isoJoiningDate = toSafeISO(joiningDate);

            const baseUser = {
                orgId,
                userType,
                email: formData.email,
                phoneNumber: formData.phoneNumber || formData.contactNumber,
                firstName: formData.firstName,
                middleName: formData.middleName || "",
                lastName: formData.lastName,
                gender: formData.gender ? formData.gender.toUpperCase() : null,
                dateOfBirth: dob,
                bloodGroup: formData.bloodGroup || null,
                nationality: formData.nationality || "India",
                religion: formData.religion || "",
                category: formData.category || "",
                lastFourDigitsOfAadhaarNumber: formData.lastFourDigitsOfAadhaarNumber ? parseInt(formData.lastFourDigitsOfAadhaarNumber) : null,
                addressDetails: [{
                    addressType: "CURRENT",
                    addressLine1: formData.currentAddress,
                    addressLine2: formData.currentAddress2 || "",
                    city: formData.city,
                    state: formData.state || "",
                    country: formData.country || "India",
                    pinCode: formData.pincode,
                    alternatePhoneNumber: formData.alternatePhoneNumber || ""
                }],
                additionalAttributes: formData.facultyType === 'NIRVAHAK' ? {
                    area: formData.area || "",
                    latitude: formData.latitude || "",
                    longitude: formData.longitude || ""
                } : {},
                userStatus: "ACTIVE",
                creationTime: currentTime,
                lastUpdatedTime: currentTime,
                createdBy: "admin",
                lastUpdatedBy: "admin"
            };

            let payload = { user: baseUser };

            if (userType === 'STUDENT') {
                payload.studentInfo = {
                    classId: formData.classId || selectedClass || "",
                    className: formData.className || "",
                    admissionId: formData.admissionId,
                    rollNumber: formData.rollNumber,
                    enrollmentStatus: "ENROLLED",
                    dateOfJoining: isoJoiningDate,
                    admissionType: "REGULAR",
                    previousSchoolDetails: formData.previousSchoolDetails || "",
                    scholarshipAwards: formData.scholarshipAwards || "",
                    transportOpted: formData.transportOpted || false,
                };
            } else if (userType === 'FACULTY') {
                payload.facultyInfo = {
                    facultyType: formData.facultyType || null,
                    employeeId: formData.employeeId || "",
                    designation: formData.designation || "",
                    department: formData.department || "",
                    joiningDate: isoJoiningDate,
                    facultyContractType: formData.facultyContractType || null,
                    responsibilities: formData.responsibilities ? [formData.responsibilities] : [],
                    supervisorId: formData.facultyType === 'SAMANVAYKARTA' ? null : (formData.supervisorId || null),
                };
            } else if (userType === 'STAFF' || userType === 'ADMIN') {
                const infoKey = 'staffInfo'; 
                payload[infoKey] = {
                    employeeId: formData.employeeId,
                    designation: formData.designation,
                    department: formData.department,
                    joiningDate: isoJoiningDate,
                    staffContractType: formData.staffContractType || 'PERMANENT',
                    responsibilities: formData.responsibilities ? [formData.responsibilities] : [],
                    identificationMark: formData.identificationMark || null,
                    drivingLicenseNumber: formData.drivingLicenseNumber || null,
                    drivingExperienceYears: formData.drivingExperienceYears ? parseInt(formData.drivingExperienceYears) : null,
                };
            }

            // 3. Register User
            console.log(`[Registration] Submitting ${userType} payload:`, payload);
            const response = await axios.post(configData.REGISTER_USER, payload);
            const userId = response.data?.userId || response.data?.user?.userId;

            if (!userId) throw new Error("Registration failed: No User ID returned");

            // 4. Special logic for Students (Parents)
            if (userType === 'STUDENT') {
                const parentPayloads = [];
                if (formData.fatherName && formData.fatherContact) {
                    parentPayloads.push({
                        user: { orgId, userType: "PARENT", email: formData.fatherEmail || `${formData.fatherContact}@p.egurukul.com`, phoneNumber: formData.fatherContact, firstName: formData.fatherName, userStatus: "ACTIVE" },
                        parentInfo: {
                            parentType: "FATHER",
                            occupation: formData.fatherOccupation || "",
                            listOfChildIds: [userId]
                        }
                    });
                }
                if (formData.motherName && formData.motherContact) {
                    parentPayloads.push({
                        user: { orgId, userType: "PARENT", email: formData.motherEmail || `${formData.motherContact}@p.egurukul.com`, phoneNumber: formData.motherContact, firstName: formData.motherName, userStatus: "ACTIVE" },
                        parentInfo: {
                            parentType: "MOTHER",
                            occupation: formData.motherOccupation || "",
                            listOfChildIds: [userId]
                        }
                    });
                }

                if (parentPayloads.length > 0) {
                    console.log(`[Registration] Creating ${parentPayloads.length} parent records...`);
                    const parentResponses = await Promise.all(parentPayloads.map(p => axios.post(configData.REGISTER_USER, p)));
                    const parentIds = parentResponses.map(r => r.data?.userId).filter(id => id);

                    // Fetch the created student and send a full update payload with parent IDs.
                    const existingStudentResponse = await axios.get(`${configData.GET_USER_DETAILS}/${userId}`);
                    const existingStudent = existingStudentResponse.data;

                    await axios.put(`${configData.UPDATE_USER}/${userId}`, {
                        user: existingStudent.user,
                        studentInfo: {
                            ...existingStudent.studentInfo,
                            listOfParentIds: parentIds
                        },
                        parentInfo: existingStudent.parentInfo || null,
                        facultyInfo: existingStudent.facultyInfo || null,
                        staffInfo: existingStudent.staffInfo || null,
                    });
                }
            }

            // 5. Send Notification (Optional, non-critical)
            try {
                const emailData = {
                    subject: "Welcome to Egurukul - Your Account Details",
                    messageBody: `Welcome to Egurukul!\n\nYour account has been successfully created.\n\nLogin Details:\nUsername: ${formData.email}\nPassword: ${defaultPassword}`,
                    userType,
                    specificUserIds: [userId],
                };
                const emailFormData = new FormData();
                emailFormData.append('request', new Blob([JSON.stringify(emailData)], { type: 'application/json' }));
                await axios.post(configData.SEND_EMAIL_NOTIFICATION, emailFormData);
            } catch (e) {
                console.warn("Email notification failed", e);
            }

            toast.success(`${userTypeLabel[userType] || userType} registered successfully!`);
            setTimeout(() => {
                if (onSuccess) {
                    onSuccess();
                } else if (onCancel) {
                    onCancel();
                } else {
                    if (finalLockedFacultyType) {
                        navigate(`/kv-faculty/${finalLockedFacultyType.toLowerCase()}`, { state: { selectedClass } });
                    } else {
                        const redirectRouteMap = {
                            STUDENT: '/Students',
                            FACULTY: '/Faculty',
                            STAFF: '/Staff',
                            ADMIN: '/Admin',
                        };
                        navigate(redirectRouteMap[userType] || '/Users', { state: { selectedClass } });
                    }
                }
            }, 2000);

        } catch (err) {
            console.error(`[Registration] CRITICAL FAILURE for ${formData.email}:`, {
                errorCode: err.response?.status,
                errorMessage: err.response?.data?.message || err.message,
                errorDetails: err.response?.data
            });
            const errorMsg = err.response?.data?.message || err.message || "Registration failed";
            toast.error(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    const userTypeLabel = {
        STUDENT: 'Student',
        FACULTY: 'Faculty',
        STAFF: 'Staff',
        ADMIN: 'Admin',
    };

    return (
        <div className="reg-page">
            {/* ── Header — only shown when NOT embedded in dialog (no onCancel from KV) */}
            {!onCancel && (
                <div className="reg-header">
                    <button className="reg-back-btn" onClick={() => navigate(-1)}>
                        ← Back
                    </button>
                    <span className="reg-header-title">Add New {userTypeLabel[userType] || userType}</span>
                    <div style={{ width: 80 }} /> {/* spacer */}
                </div>
            )}

            {/* ── Alerts ── */}


            {/* ── Form ── */}
            <LocalizationProvider dateAdapter={AdapterDayjs}>
            <form className="reg-form" onSubmit={handleSubmit}>
                {currentFields.map((section, sIdx) => (
                    <div className="reg-section" key={sIdx} style={{ animationDelay: `${sIdx * 0.06}s` }}>
                        <div className="reg-section-title">
                            <span className="reg-section-icon">
                                {sectionIcons[section.section] || '📄'}
                            </span>
                            {section.section}
                        </div>
                        <div className="reg-fields-grid">
                            {section.fields.map((field, fIdx) => {
                                if (['area', 'latitude', 'longitude'].includes(field.name) && formData.facultyType !== 'NIRVAHAK') {
                                    return null;
                                }
                                return (
                                    <div className="reg-field-wrapper" key={fIdx} style={{ animationDelay: `${(sIdx * 0.06) + (fIdx * 0.03)}s` }}>
                                    {/* ── supervisorId field: special hierarchy rendering ── */}
                                    {field.name === 'supervisorId' && userType === 'FACULTY' ? (
                                        <>
                                            {supervisorDisabled ? (
                                                // SAMANVAYKARTA — no supervisor needed
                                                <Box
                                                    display="flex"
                                                    alignItems="center"
                                                    gap={1}
                                                    sx={{
                                                        bgcolor: '#e8f4fd',
                                                        border: '1px solid #90caf9',
                                                        borderRadius: 2,
                                                        px: 2,
                                                        py: 1.2,
                                                        minHeight: 40,
                                                    }}
                                                >
                                                    <InfoOutlinedIcon sx={{ color: '#1565c0', fontSize: 18 }} />
                                                    <span style={{ fontSize: 13, color: '#1565c0', fontWeight: 600 }}>
                                                        Samanvaykarta is the top level — no supervisor required.
                                                    </span>
                                                </Box>
                                            ) : supervisorIsDropdown ? (
                                                // PARYAVEKSHAK or NIRVAHAK — show dropdown
                                                <FormControl fullWidth size="small" required={field.required}>
                                                    <InputLabel>
                                                        {formData.facultyType === 'NIRVAHAK'
                                                            ? 'Supervisor (Paryavekshak/Samanvaykarta)'
                                                            : 'Supervisor (Samanvaykarta)'}
                                                    </InputLabel>
                                                    <Select
                                                        name="supervisorId"
                                                        value={formData.supervisorId || ''}
                                                        label={
                                                            formData.facultyType === 'NIRVAHAK'
                                                                ? 'Supervisor (Paryavekshak/Samanvaykarta)'
                                                                : 'Supervisor (Samanvaykarta)'
                                                        }
                                                        onChange={handleChange}
                                                        disabled={supervisorLoading || !!stateSupervisorId}
                                                        endAdornment={
                                                            supervisorLoading
                                                                ? <CircularProgress size={16} sx={{ mr: 2 }} />
                                                                : null
                                                        }
                                                    >
                                                        <MenuItem value="">
                                                            <em style={{ color: '#94a3b8' }}>
                                                                {supervisorLoading
                                                                    ? 'Loading supervisors…'
                                                                    : supervisorOptions.length === 0
                                                                    ? `No ${supervisorFacultyLabel} found`
                                                                    : `Select ${supervisorFacultyLabel}`}
                                                            </em>
                                                        </MenuItem>
                                                        {supervisorOptions.map(sup => (
                                                            <MenuItem key={sup.id} value={sup.id}>
                                                                <Box>
                                                                    <span style={{ fontWeight: 600 }}>{sup.name}</span>
                                                                    {sup.employeeId && (
                                                                        <Chip
                                                                            label={sup.employeeId}
                                                                            size="small"
                                                                            sx={{ ml: 1, fontSize: 11, height: 18 }}
                                                                        />
                                                                    )}
                                                                    {sup.phone && (
                                                                        <span style={{ fontSize: 12, color: '#64748b', marginLeft: 6 }}>
                                                                            {sup.phone}
                                                                        </span>
                                                                    )}
                                                                </Box>
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                    {supervisorOptions.length === 0 && !supervisorLoading && formData.facultyType && (
                                                        <FormHelperText sx={{ color: 'warning.main' }}>
                                                            No {supervisorFacultyLabel} registered yet. Register one first.
                                                        </FormHelperText>
                                                    )}
                                                    {stateSupervisorId && (
                                                        <FormHelperText>
                                                            Supervisor is automatically assigned from the parent context.
                                                        </FormHelperText>
                                                    )}
                                                </FormControl>
                                            ) : (
                                                // Non-KV org — plain text input (existing behavior)
                                                <TextField
                                                    fullWidth
                                                    label={field.label}
                                                    name={field.name}
                                                    value={formData[field.name] || ""}
                                                    onChange={handleChange}
                                                    required={field.required}
                                                    size="small"
                                                    disabled={!!stateSupervisorId}
                                                    helperText={stateSupervisorId ? "Supervisor is automatically assigned from the parent context." : ""}
                                                    InputProps={{
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                <Tooltip
                                                                    title={
                                                                        <div style={{ padding: '4px', fontSize: '12px', lineHeight: '1.5' }}>
                                                                            <strong style={{ display: 'block', marginBottom: '4px' }}>Supervisor ID Hierarchy:</strong>
                                                                            • <strong>Nirvahak</strong>: Enter their Paryavekshak's Phone, Email, or Employee ID.<br />
                                                                            • <strong>Paryavekshak</strong>: Enter their Samanvaykarta's Phone, Email, or Employee ID.<br />
                                                                            • <strong>Samanvaykarta</strong>: No supervisor required.
                                                                        </div>
                                                                    }
                                                                    arrow
                                                                    placement="top"
                                                                >
                                                                    <HelpOutlineIcon sx={{ cursor: 'help', color: 'text.secondary', fontSize: 18 }} />
                                                                </Tooltip>
                                                            </InputAdornment>
                                                        )
                                                    }}
                                                />
                                            )}
                                        </>
                                    ) : field.type === 'classSelect' ? (
                                        <FormControl fullWidth size="small" required={field.required}>
                                            <InputLabel id="class-select-label">Class / Section</InputLabel>
                                            <Select
                                                labelId="class-select-label"
                                                name="classId"
                                                value={formData.classId || ""}
                                                label="Class / Section"
                                                onChange={handleChange}
                                                disabled={classesLoading}
                                                endAdornment={
                                                    classesLoading ? <CircularProgress size={16} sx={{ mr: 2 }} /> : null
                                                }
                                            >
                                                <MenuItem value="">
                                                    <em style={{ color: '#94a3b8' }}>
                                                        {classesLoading ? 'Loading classes…' : 'Select Class / Section'}
                                                    </em>
                                                </MenuItem>
                                                {classesList.map(cls => (
                                                    <MenuItem key={cls.id} value={cls.id}>
                                                        {cls.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    ) : field.type === 'select' ? (
                                        // When finalLockedFacultyType is set for the facultyType field, show it read-only
                                        finalLockedFacultyType && field.name === 'facultyType' ? (
                                            <TextField
                                                select
                                                fullWidth
                                                label={field.label}
                                                name={field.name}
                                                value={formData[field.name] || finalLockedFacultyType || ""}
                                                onChange={handleChange}
                                                required={field.required}
                                                size="small"
                                                disabled
                                                helperText="Faculty type is fixed for this operation"
                                            >
                                                <MenuItem value={finalLockedFacultyType}>{finalLockedFacultyType.replace(/_/g, ' ')}</MenuItem>
                                            </TextField>
                                        ) : (
                                        <TextField
                                            select
                                            fullWidth
                                            label={field.label}
                                            name={field.name}
                                            value={formData[field.name] || ""}
                                            onChange={handleChange}
                                            required={field.required}
                                            size="small"
                                        >
                                            <MenuItem value="">Select {field.label}</MenuItem>
                                            {field.options.map(opt => (
                                                <MenuItem key={opt} value={opt}>{opt.replace(/_/g, ' ')}</MenuItem>
                                            ))}
                                        </TextField>
                                        )
                                    ) : field.type === 'checkbox' ? (
                                        <FormControlLabel
                                            control={<Checkbox checked={!!formData[field.name]} onChange={handleChange} name={field.name} />}
                                            label={field.label}
                                        />
                                    ) : field.type === 'date' ? (
                                        <DatePicker
                                            label={field.label}
                                            value={formData[field.name] ? dayjs(formData[field.name]) : null}
                                            onChange={(newValue) => {
                                                const event = {
                                                    target: {
                                                        name: field.name,
                                                        value: newValue ? newValue.format('YYYY-MM-DD') : '',
                                                        type: 'date'
                                                    }
                                                };
                                                handleChange(event);
                                            }}
                                            format="DD/MM/YYYY"
                                            openTo={field.name === 'dob' || field.name === 'dateOfBirth' ? 'year' : 'day'}
                                            views={['year', 'month', 'day']}
                                            slotProps={{
                                                textField: {
                                                    fullWidth: true,
                                                    size: "small",
                                                    required: field.required,
                                                }
                                            }}
                                        />
                                    ) : (
                                        <>
                                            <TextField
                                                fullWidth
                                                type={PHONE_FIELD_NAMES.includes(field.name) ? "tel" : field.type}
                                                label={field.label}
                                                name={field.name}
                                                value={formData[field.name] || ""}
                                                onChange={handleChange}
                                                onKeyDown={(e) => handleKeyDown(e, field)}
                                                onBlur={() => handleBlur(field)}
                                                required={field.required}
                                                size="small"
                                                inputProps={{
                                                    ...(field.maxLength ? { maxLength: field.maxLength } : {}),
                                                    ...(PHONE_FIELD_NAMES.includes(field.name) ? { maxLength: 10, inputMode: 'numeric', pattern: '[0-9]{10}' } : {})
                                                }}
                                                error={!!(duplicateErrors[field.name] || fieldErrors[field.name])}
                                                helperText={duplicateErrors[field.name] || fieldErrors[field.name] || ''}
                                            />
                                            {field.name === 'longitude' && formData.facultyType === 'NIRVAHAK' && (
                                                <>
                                                    <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center' }}>
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                if (navigator.geolocation) {
                                                                    navigator.geolocation.getCurrentPosition(
                                                                        (position) => {
                                                                            setFormData(prev => ({
                                                                                ...prev,
                                                                                latitude: String(position.coords.latitude),
                                                                                longitude: String(position.coords.longitude),
                                                                            }));
                                                                        },
                                                                        (error) => {
                                                                            alert('Failed to get location: ' + error.message);
                                                                        }
                                                                    );
                                                                } else {
                                                                    alert('Geolocation is not supported by this browser.');
                                                                }
                                                            }}
                                                            style={{
                                                                padding: '6px 12px',
                                                                backgroundColor: '#e0ecfb',
                                                                border: '1.5px solid #0B4A8B',
                                                                borderRadius: '6px',
                                                                color: '#0B4A8B',
                                                                fontWeight: '600',
                                                                cursor: 'pointer',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '6px',
                                                                fontSize: '13px'
                                                            }}
                                                        >
                                                            📍 Use Current Location
                                                        </button>
                                                    </div>


                                                </>
                                            )}
                                        </>
                                    )}
                                </div>
                                );
                            })}
                        </div>
                    </div>
                ))}

                {/* ── Action Buttons ── */}
                <div className="reg-actions">
                    <button type="button" className="reg-cancel-btn" onClick={() => onCancel ? onCancel() : navigate(-1)} disabled={loading}>
                        Cancel
                    </button>
                    <button type="submit" className="reg-submit-btn" disabled={loading}>
                        {loading && <span className="reg-submit-spinner" />}
                        {loading ? 'Registering...' : 'Submit'}
                    </button>
                </div>
            </form>
            </LocalizationProvider>
        </div>
    );
};

export default UserRegistrationModule;
